import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { FadeUp } from "@/components/Reveal";
import { ArrowButton } from "@/components/ArrowButton";
import JourneyCalendar from "@/components/JourneyCalendar";
import StatusBadge from "@/components/admin/StatusBadge";
import { formatDateLong } from "@/pages/Agendar";
import { waLink } from "@/data/site";
import { CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Pacote = () => {
  const [code, setCode] = useState("");
  const [pkg, setPkg] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [hold, setHold] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (code.trim().length < 6) return toast.error("Informe o código do pacote (ex.: PAC-XXXXX).");
    setBusy(true);
    try {
      const [pkgRes, schRes] = await Promise.all([
        axios.get(`${API}/api/journey/packages/${code.trim()}`),
        axios.get(`${API}/api/journey/schedule`),
      ]);
      setPkg(pkgRes.data);
      setSchedule(schRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Pacote não encontrado.");
    } finally {
      setBusy(false);
    }
  };

  const releaseHold = async () => {
    const current = hold;
    setHold(null);
    if (current) {
      try {
        await axios.delete(`${API}/api/journey/holds/${current.hold_id}`, { params: { token: current.hold_token } });
      } catch {
        /* expira sozinha */
      }
    }
  };

  const chooseDate = (d) => {
    releaseHold();
    setTime(null);
    setDate(d);
    setAvailability(null);
    axios
      .get(`${API}/api/journey/availability`, { params: { date: d } })
      .then((res) => setAvailability(res.data.times))
      .catch(() => setAvailability([]));
  };

  const chooseTime = async (t) => {
    if (time === t) return;
    await releaseHold();
    setTime(null);
    try {
      const res = await axios.post(`${API}/api/journey/holds`, { service_slug: pkg.service_slug, date, time: t });
      setHold({ hold_id: res.data.hold_id, hold_token: res.data.hold_token });
      setTime(t);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Este horário acabou de ser reservado.");
      chooseDate(date);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/journey/packages/${pkg.code}/book`, {
        date,
        time,
        hold_id: hold?.hold_id || null,
      });
      setDone(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível agendar. Tente novamente.");
      if (err.response?.status === 409) {
        setTime(null);
        setHold(null);
        chooseDate(date);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO title="Meu pacote — agendar próxima sessão | Sabrina Mota" description="Agende a próxima sessão do seu pacote com o código recebido." path="/pacote" />
      <section className="pt-28 lg:pt-36" data-testid="pacote-page">
        <div className="container-x max-w-3xl pb-24">
          <FadeUp>
            <p className="eyebrow">Meu pacote</p>
            <h1 className="mt-5 font-serif text-3xl font-light leading-[1.1] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Agendar a <em className="italic text-raiz-700">próxima sessão</em>
            </h1>
          </FadeUp>

          {done ? (
            <FadeUp className="mt-12 text-center" data-testid="pacote-success">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-gold-400/60 bg-gold-100/50">
                <CheckCircle2 aria-hidden="true" className="h-8 w-8 text-gold-700" />
              </span>
              <h2 className="mt-8 font-serif text-3xl font-light text-ink">
                Sessão {done.session_n}/{done.sessions} agendada
              </h2>
              <p className="lead mx-auto mt-5 max-w-md">
                {formatDateLong(done.date)} às {done.time}. Protocolo <span className="font-medium text-gold-700">{done.protocol}</span>.
              </p>
              <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <ArrowButton href={waLink(`Olá, Sabrina! Agendei a sessão ${done.session_n}/${done.sessions} do meu pacote (${done.protocol}).`)} target="_blank" rel="noopener noreferrer" testId="pacote-whatsapp-button">
                  Avisar no WhatsApp
                </ArrowButton>
                <ArrowButton to="/" variant="outline" testId="pacote-home-button">
                  Voltar ao início
                </ArrowButton>
              </div>
            </FadeUp>
          ) : !pkg ? (
            <FadeUp delay={0.1}>
              <form onSubmit={search} className="mt-10 rounded-3xl border border-raiz-900/10 bg-cream p-7 sm:p-9" data-testid="pacote-form">
                <label htmlFor="pacote-codigo" className="block text-sm font-medium text-ink">
                  Código do pacote
                </label>
                <p className="mt-1 text-xs text-ink/55">Você recebe este código da Sabrina após a confirmação do pacote.</p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <input
                    id="pacote-codigo"
                    data-testid="pacote-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="PAC-XXXXX"
                    className="w-full rounded-2xl border border-raiz-800/20 bg-paper px-5 py-3.5 text-sm uppercase tracking-widest text-ink placeholder:normal-case placeholder:tracking-normal placeholder:text-ink/35 focus:border-raiz-700 focus:outline-none"
                  />
                  <button type="submit" data-testid="pacote-buscar-button" disabled={busy} className="rounded-full bg-raiz-800 px-7 py-3.5 text-sm font-medium text-paper transition-colors hover:bg-raiz-700 disabled:opacity-60">
                    {busy ? "Buscando…" : "Buscar pacote"}
                  </button>
                </div>
              </form>
            </FadeUp>
          ) : (
            <FadeUp delay={0.08} className="mt-10 space-y-8" data-testid="pacote-summary">
              <div className="rounded-3xl border border-raiz-900/10 bg-cream p-7">
                <p className="text-xs uppercase tracking-[0.24em] text-raiz-600">{pkg.code}</p>
                <h2 className="mt-3 font-serif text-2xl font-light text-ink">
                  Olá, <em className="italic text-raiz-700">{pkg.first_name}</em> — {pkg.service_title}
                </h2>
                <p className="mt-2 text-sm text-ink/60">{pkg.plan_name}</p>
                <div className="mt-5 grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
                  <div className="rounded-2xl border border-raiz-900/10 bg-paper p-3">
                    <p className="font-serif text-2xl text-raiz-900" data-testid="pacote-contratadas">{pkg.contratadas}</p>
                    <p className="text-[10px] uppercase tracking-wide sm:tracking-widest text-ink/50">Contratadas</p>
                  </div>
                  <div className="rounded-2xl border border-raiz-900/10 bg-paper p-3">
                    <p className="font-serif text-2xl text-raiz-900" data-testid="pacote-agendadas">{pkg.agendadas}</p>
                    <p className="text-[10px] uppercase tracking-wide sm:tracking-widest text-ink/50">Agendadas</p>
                  </div>
                  <div className="rounded-2xl border border-raiz-900/10 bg-paper p-3">
                    <p className="font-serif text-2xl text-raiz-900" data-testid="pacote-utilizadas">{pkg.utilizadas}</p>
                    <p className="text-[10px] uppercase tracking-wide sm:tracking-widest text-ink/50">Utilizadas</p>
                  </div>
                  <div className="rounded-2xl border border-gold-400/50 bg-gold-100/40 p-3">
                    <p className="font-serif text-2xl text-gold-700" data-testid="pacote-restantes">{pkg.restantes}</p>
                    <p className="text-[10px] uppercase tracking-wide sm:tracking-widest text-ink/50">Restantes</p>
                  </div>
                </div>
                <ul className="mt-5 space-y-2" data-testid="pacote-sessoes-lista">
                  {pkg.sessions.map((s) => (
                    <li key={s.session_n} className="flex items-center justify-between gap-3 text-sm" data-testid={`pacote-sessao-${s.session_n}`}>
                      <span className="text-ink/70">
                        Sessão {s.session_n}/{pkg.contratadas} — {formatDateLong(s.date)} às {s.time}
                      </span>
                      <StatusBadge status={s.status === "falta" ? "cancelada" : s.status} />
                    </li>
                  ))}
                </ul>
              </div>

              {pkg.status !== "ativo" ? (
                <p className="rounded-2xl border border-raiz-900/10 bg-cream p-5 text-sm text-ink/65" data-testid="pacote-inativo">
                  Este pacote está {pkg.status === "concluido" ? "concluído" : "cancelado"}. Para renovar, converse com a Sabrina pelo WhatsApp.
                </p>
              ) : pkg.restantes <= 0 ? (
                <p className="rounded-2xl border border-raiz-900/10 bg-cream p-5 text-sm text-ink/65">
                  Todas as sessões já foram utilizadas. Para renovar o pacote, converse com a Sabrina pelo WhatsApp.
                </p>
              ) : (
                <div className="rounded-3xl border border-raiz-900/10 bg-paper p-7" data-testid="pacote-agendar">
                  <h3 className="font-serif text-xl text-ink">Escolha data e horário</h3>
                  <div className="mt-5">
                    <JourneyCalendar schedule={schedule} selected={date} onSelect={chooseDate} />
                  </div>
                  {date && (
                    <div className="mt-6">
                      {!availability ? (
                        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                          {[...Array(8)].map((_, i) => (
                            <div key={i} className="h-12 animate-pulse rounded-full bg-cream" />
                          ))}
                        </div>
                      ) : availability.length === 0 ? (
                        <p className="text-sm text-ink/60">Sem horários nesta data. Escolha outro dia.</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                          {availability.map((t) => (
                            <button
                              key={t.time}
                              type="button"
                              data-testid={`pacote-horario-${t.time.replace(":", "")}`}
                              disabled={!t.available}
                              onClick={() => chooseTime(t.time)}
                              className={`rounded-full border py-3 text-sm transition-colors duration-300 ${
                                time === t.time
                                  ? "border-raiz-800 bg-raiz-800 font-medium text-paper"
                                  : t.available
                                    ? "border-raiz-800/20 text-raiz-900 hover:border-gold-500 hover:text-gold-700"
                                    : "cursor-not-allowed border-raiz-900/5 text-ink/25 line-through"
                              }`}
                            >
                              {t.time}
                            </button>
                          ))}
                        </div>
                      )}
                      {time && (
                        <p className="mt-4 rounded-2xl border border-gold-400/40 bg-gold-100/40 p-4 text-sm text-ink/75" data-testid="pacote-reserva-note">
                          Horário reservado para você por 20 minutos.
                        </p>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid="pacote-confirmar-button"
                    onClick={confirm}
                    disabled={busy || !time}
                    className="mt-7 rounded-full bg-raiz-800 px-8 py-3.5 text-sm font-medium text-paper transition-colors hover:bg-raiz-700 disabled:opacity-50"
                  >
                    {busy ? "Confirmando…" : "Confirmar sessão"}
                  </button>
                </div>
              )}
              <p className="text-center text-xs text-ink/50">
                Precisa de ajuda? <Link to="/contato" className="text-raiz-800 underline underline-offset-4">Fale comigo</Link>
              </p>
            </FadeUp>
          )}
        </div>
      </section>
    </>
  );
};

export default Pacote;
